// Client-side mirror of server/utils/typography/validateTypography.js, for
// instant feedback while editing. The server stays authoritative.
//
// Returns { [field]: { level: 'error' | 'warning' | 'info', message } } for one role.

const isBlank = (v) => v === '' || v === null || v === undefined;

export function checkRole(role, value = {}, limits) {
  const out = {};

  [
    ['sizeDesktop', 'desktop'],
    ['sizeMobile', 'mobile'],
  ].forEach(([field, side]) => {
    const raw = value[field];
    const n = Number(raw);
    const [lo, hi] = role.recommended?.[side] || [];
    const rec = lo === hi ? `Recommended ${lo}px` : `Recommended ${lo}–${hi}px`;
    const floor = Math.max(limits.sizeMin, role.minPx || 0);

    if (isBlank(raw) || !Number.isFinite(n)) {
      out[field] = { level: 'error', message: 'Enter a size' };
    } else if (n < floor) {
      out[field] = {
        level: 'error',
        message: role.minPx ? `Minimum ${role.minPx}px (accessibility)` : `Minimum ${limits.sizeMin}px`,
      };
    } else if (n > limits.sizeMax) {
      out[field] = { level: 'error', message: `Maximum ${limits.sizeMax}px` };
    } else if (lo !== undefined && (n < lo || n > hi)) {
      out[field] = { level: 'warning', message: `Outside spec · ${rec.toLowerCase()}` };
    } else {
      out[field] = { level: 'info', message: rec };
    }
  });

  const lh = Number(value.lineHeight);
  if (isBlank(value.lineHeight) || !Number.isFinite(lh)) {
    out.lineHeight = { level: 'error', message: 'Enter a line height' };
  } else if (lh < limits.lineHeightMin || lh > limits.lineHeightMax) {
    out.lineHeight = { level: 'error', message: `${limits.lineHeightMin}–${limits.lineHeightMax}` };
  }

  return out;
}

const roleHasErrors = (role, value, limits) =>
  Object.values(checkRole(role, value, limits)).some((i) => i.level === 'error');

// Site-wide roles plus every page-group override.
export function hasBlockingErrors(roles, registry, limits, groups = {}) {
  return (
    registry.some((role) => roleHasErrors(role, roles?.[role.key], limits)) ||
    Object.values(groups).some((overrides) =>
      registry.some((role) => overrides?.[role.key] && roleHasErrors(role, overrides[role.key], limits))
    )
  );
}

// Normalize one role's editor state (inputs hold strings while typing).
const toRolePayload = (v = {}) => ({
  sizeDesktop: Number(v.sizeDesktop),
  sizeMobile: Number(v.sizeMobile),
  lineHeight: Number(v.lineHeight),
  weight: Number(v.weight),
  textTransform: v.textTransform,
});

export function toPayload(roles, registry) {
  const out = {};
  registry.forEach(({ key }) => {
    out[key] = toRolePayload(roles?.[key]);
  });
  return out;
}

// { [groupKey]: { [roleKey]: values } } — only overridden roles, empty groups dropped.
export function toGroupsPayload(groups, registry) {
  const out = {};
  Object.entries(groups || {}).forEach(([groupKey, overrides]) => {
    const g = {};
    registry.forEach(({ key, siteWideOnly }) => {
      if (!siteWideOnly && overrides?.[key]) g[key] = toRolePayload(overrides[key]);
    });
    if (Object.keys(g).length) out[groupKey] = g;
  });
  return out;
}

const FIELDS = ['sizeDesktop', 'sizeMobile', 'lineHeight', 'weight', 'textTransform'];
const sameRole = (a, b) => FIELDS.every((f) => String(a?.[f] ?? '') === String(b?.[f] ?? ''));

// Deep-ish equality on the five editable fields (string "18" equals 18).
export function sameRoles(a, b, registry) {
  return registry.every(({ key }) => sameRole(a?.[key], b?.[key]));
}

export function sameGroups(a = {}, b = {}, registry) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  return [...keys].every((g) =>
    registry.every(({ key }) => {
      const x = a?.[g]?.[key];
      const y = b?.[g]?.[key];
      return (!x && !y) || (x && y && sameRole(x, y));
    })
  );
}

// Effective values on a group's pages: site-wide roles with its overrides on top.
export const mergeGroupRoles = (roles, overrides) => ({ ...roles, ...(overrides || {}) });
